import {Injectable} from '@angular/core';
import {EMPTY, map, Observable, of, take, tap} from 'rxjs';
import {HttpClient, HttpParams, HttpStatusCode} from '@angular/common/http';
import {catchError} from 'rxjs/operators';
import {TranslocoService} from '@jsverse/transloco';
import {ApiService} from '@core/api/api.service';
import {AutoCompleteResult, PaginatedResult, PaginatedSearch} from '@core/api/common';
import {CreateActivityForHostRequest, HostDTO, HostStats, UpdateHostRequest} from '@domain/host/host.dto';
import {NotificationService} from '@shared/notification/notification.service';
import {MemberRole} from '@domain/member/member.dto';
import {ActivityDTO} from '@domain/activity/activity.dto';
import {ApiUtil} from '@shared/util/apiUtil';

@Injectable({
  providedIn: 'root'
})
export class HostApi {

  protected readonly apiPrefix = 'host';

  private static ERROR_HOST_DRAFT_ACTIVITY_LIMIT = 'TooManyPendingActivitiesForHost';
  private static ERROR_USER_DRAFT_ACTIVITY_LIMIT = 'TooManyPendingActivitiesForUser';

  constructor(
    private readonly http: HttpClient,
    private readonly notifyService: NotificationService,
    private readonly transloco: TranslocoService,
    private readonly apiService: ApiService
  ) { }

  public get(hostId?: string): Observable<HostDTO | null> {
    if (!hostId) {
      return of(null);
    }
    return this.apiService.getOne<HostDTO>(ApiUtil.buildUrl(this.getPath(hostId)));
  }

  public autocomplete(term?: string): Observable<AutoCompleteResult<Partial<HostDTO>>> {
    return this.apiService.autocomplete<Partial<HostDTO>>(this.apiPrefix, term);
  }

  public list(options: PaginatedSearch = {}): Observable<PaginatedResult<HostDTO>> {
    return this.apiService.paginateGet<HostDTO>(ApiUtil.buildUrl(this.getPath()), options);
  }

  public search(term?: string): Observable<HostDTO[]> {
    return this.http.get<HostDTO[]>(ApiUtil.buildUrl(this.getPath(), `/search/${term}`));
  }

  public create(host: Partial<UpdateHostRequest>): Observable<HostDTO | null> {
    return this.http.post<HostDTO>(ApiUtil.buildUrl(this.getPath()), host).pipe(
      catchError(response => {
        if (response?.status === HttpStatusCode.Conflict) {
          if (response?.error?.message) {
            this.notifyService.toast({
              message: this.transloco.translate(`activity.publish.error.${response?.error?.message}`)
            });
          } else {
            this.notifyService.toast({
              message: response?.message
            });
          }
        }
        return of(null);
      })
    );
  }

  public update(hostId: string, host: Partial<UpdateHostRequest>): Observable<HostDTO> {
    return this.http.patch<HostDTO>(ApiUtil.buildUrl(this.getPath(hostId)), host);
  }

  public delete(hostId: string, deleteOrphanUsers: boolean): Observable<boolean> {
    const params = new HttpParams().set('deleteOrphanUsers', deleteOrphanUsers);
    return this.http.delete<void>(ApiUtil.buildUrl(this.getPath(hostId)), {params: params}).pipe(
      catchError(response => {
        this.notifyService.toast({
          message: `An error occured: ${response?.error?.message ?? response?.message}`
        });
        return EMPTY;
      }),
      tap(() => {
        this.notifyService.toast({
          message: 'L\'organisation a été supprimée.'
        });
      }),
      map(() => true)
    );
  }

  public getCoverThumbnailUrl(hostId: string): string {
    return ApiUtil.buildUrl(this.getPath(hostId), '/cover/thumbnail');
  }

  public getCoverUrl(hostId: string): string {
    return ApiUtil.buildUrl(this.getPath(hostId), '/cover');
  }

  public createActivity(host: HostDTO, roles?: MemberRole[]): Observable<ActivityDTO | null> {
    //TODO: see https://hopla.atlassian.net/browse/HOPLA-143
    const createRequest: CreateActivityForHostRequest = {};
    if (roles !== undefined) {
      createRequest.roles = roles;
    }
    return this.http.post<ActivityDTO>(ApiUtil.buildUrl(this.getPath(host.id), '/activity'), createRequest).pipe(
      catchError(response => {
        if (response?.status === HttpStatusCode.Conflict) {
          const error = response?.error?.message;
          let msg = response?.message;
          if (error === HostApi.ERROR_HOST_DRAFT_ACTIVITY_LIMIT) {
            msg = this.transloco.translate(
              'activity.publish.error.TooManyPendingActivitiesForHost',
              {host: host.name}
            );
          } else if (error === HostApi.ERROR_USER_DRAFT_ACTIVITY_LIMIT) {
            msg = this.transloco.translate('activity.publish.error.TooManyPendingActivitiesForUser');
          }
          this.notifyService.toast({
            message: msg
          });
        }
        return of(null);
      })
    );
  }

  public resendValidationEmail(hostId: string): Observable<boolean> {
    return this.http
      .post<void>(
        ApiUtil.buildUrl(this.getPath(hostId), '/resend-validation-email'),
        null,
        {observe: 'response'}
      )
      .pipe(
        map(response => response?.status === HttpStatusCode.NoContent),
        catchError(_err => of(false)),
        take(1)
      );
  }

  public getStats(): Observable<HostStats> {
    return this.http.get<HostStats>(ApiUtil.buildUrl(this.getPath(), `/stats`));
  }

  private getPath(hostId?: string): string {
    const prefix = 'host';
    return hostId
      ? `${prefix}/${hostId}`
      : prefix;
  }

}
